import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import { TESTIMONIALS, TESTIMONIAL_IMAGES } from "../lib/site";

const initials = (name) =>
  name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

export default function Testimonials() {
  const trackRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [perView, setPerView] = useState(3);

  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      setPerView(w >= 1024 ? 3 : w >= 640 ? 2 : 1);
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const maxIndex = Math.max(0, TESTIMONIALS.length - perView);
  const goto = (i) => setIndex(Math.max(0, Math.min(maxIndex, i)));

  return (
    <section
      id="testimonials"
      className="py-20 lg:py-28 bg-[#FAFAFA]"
      data-testid="testimonials-section"
    >
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <p className="tc-eyebrow mb-3">Testimonials</p>
            <h2 className="font-serif text-[34px] sm:text-[44px] lg:text-[52px] text-[#173B86] leading-[1.05] tracking-[-0.01em]">
              Loved by Homeowners
            </h2>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <button
              onClick={() => goto(index - 1)}
              className="w-10 h-10 rounded-full border border-[#dde3ed] flex items-center justify-center hover:bg-white hover:border-[#183B87] transition disabled:opacity-40"
              disabled={index === 0}
              aria-label="Previous"
              data-testid="testimonials-prev"
            >
              <ChevronLeft className="w-4 h-4 text-[#173B86]" />
            </button>
            <button
              onClick={() => goto(index + 1)}
              className="w-10 h-10 rounded-full border border-[#dde3ed] flex items-center justify-center hover:bg-white hover:border-[#183B87] transition disabled:opacity-40"
              disabled={index >= maxIndex}
              aria-label="Next"
              data-testid="testimonials-next"
            >
              <ChevronRight className="w-4 h-4 text-[#173B86]" />
            </button>
          </div>
        </div>

        <div className="overflow-hidden">
          <div
            ref={trackRef}
            className="flex transition-transform duration-500 ease-out gap-5 lg:gap-6"
            style={{
              transform: `translateX(calc(${-index * (100 / perView)}% - ${
                index * (perView === 1 ? 20 : 24)
              }px))`,
            }}
          >
            {TESTIMONIALS.map((t, i) => {
              const img = TESTIMONIAL_IMAGES[i % TESTIMONIAL_IMAGES.length];
              return (
                <article
                  key={i}
                  className="bg-white rounded-[20px] p-5 sm:p-6 shadow-sm border border-[#eef1f7] flex-shrink-0 flex flex-col"
                  style={{
                    width: `calc(${100 / perView}% - ${perView === 1 ? 0 : (24 * (perView - 1)) / perView}px)`,
                  }}
                  data-testid={`testimonial-${i}`}
                >
                  <img
                    src={img}
                    alt="Cleaning before and after"
                    className="rounded-lg w-full h-40 sm:h-44 object-cover mb-4"
                    loading="lazy"
                  />
                  <div className="flex items-center gap-0.5 mb-2.5">
                    {Array.from({ length: t.rating }).map((_, k) => (
                      <Star key={k} className="w-3.5 h-3.5 fill-[#F5B700] text-[#F5B700]" />
                    ))}
                    {Array.from({ length: 5 - t.rating }).map((_, k) => (
                      <Star key={`e${k}`} className="w-3.5 h-3.5 text-[#d8dde6]" />
                    ))}
                  </div>
                  <p className="text-[13.5px] leading-[1.65] text-[#1A2B4A]/90 mb-4 flex-1">
                    "{t.quote}"
                  </p>
                  <div className="flex items-center gap-3 pt-3 border-t border-[#eef1f7]">
                    <div className="w-9 h-9 rounded-full bg-[#DCEBFB] text-[#173B86] text-[12px] font-semibold flex items-center justify-center">
                      {initials(t.name)}
                    </div>
                    <div>
                      <p className="text-[13px] font-semibold text-[#1A2B4A]">
                        {t.name}
                      </p>
                      <p className="text-[11.5px] text-[#5b6a85]">Verified customer</p>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>

        {/* Mobile dots / arrows */}
        <div className="flex sm:hidden items-center justify-center gap-2 mt-8">
          <button
            onClick={() => goto(index - 1)}
            className="w-9 h-9 rounded-full border border-[#dde3ed] flex items-center justify-center disabled:opacity-40"
            disabled={index === 0}
            aria-label="Previous"
          >
            <ChevronLeft className="w-4 h-4 text-[#173B86]" />
          </button>
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button
              key={i}
              onClick={() => goto(i)}
              aria-label={`Go to ${i + 1}`}
              className={`w-2 h-2 rounded-full transition ${
                i === index ? "bg-[#183B87] w-5" : "bg-[#cdd6e6]"
              }`}
            />
          ))}
          <button
            onClick={() => goto(index + 1)}
            className="w-9 h-9 rounded-full border border-[#dde3ed] flex items-center justify-center disabled:opacity-40"
            disabled={index >= maxIndex}
            aria-label="Next"
          >
            <ChevronRight className="w-4 h-4 text-[#173B86]" />
          </button>
        </div>
      </div>
    </section>
  );
}
